import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WatchlistWidget = ({ onSymbolSelect, selectedSymbol }) => {
  const [watchlist, setWatchlist] = useState([
    {
      symbol: 'BTC/USD',
      price: 43275.50,
      change: 2.45,
      alert: { enabled: true, price: 45000, type: 'above' }
    },
    {
      symbol: 'ETH/USD',
      price: 2650.75,
      change: -1.23,
      alert: { enabled: true, price: 2500, type: 'below' }
    },
    {
      symbol: 'ADA/USD',
      price: 0.52,
      change: 4.67,
      alert: { enabled: false, price: null, type: null }
    },
    {
      symbol: 'SOL/USD',
      price: 98.45,
      change: -3.21,
      alert: { enabled: true, price: 100, type: 'above' }
    }
  ]);
  
  const [newSymbol, setNewSymbol] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const addToWatchlist = () => {
    if (newSymbol && !watchlist?.find(item => item?.symbol === newSymbol)) {
      const newItem = {
        symbol: newSymbol?.toUpperCase(),
        price: Math.random() * 1000 + 100,
        change: (Math.random() - 0.5) * 10,
        alert: { enabled: false, price: null, type: null }
      };
      setWatchlist([...watchlist, newItem]);
      setNewSymbol('');
      setShowAddForm(false);
    }
  };

  const removeFromWatchlist = (symbol) => {
    setWatchlist(watchlist?.filter(item => item?.symbol !== symbol));
  };

  const toggleAlert = (symbol) => {
    setWatchlist(watchlist?.map(item => 
      item?.symbol === symbol 
        ? { ...item, alert: { ...item?.alert, enabled: !item?.alert?.enabled } }
        : item
    ));
  };

  const formatPrice = (price) => {
    if (price >= 1) return `$${price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price?.toFixed(6)}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Watchlist</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <Icon name="Plus" size={16} />
          Add
        </Button>
      </div>
      {/* Add Symbol Form */}
      {showAddForm && (
        <div className="bg-muted rounded-lg p-3 mb-4">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="e.g., DOGE/USD"
              value={newSymbol}
              onChange={(e) => setNewSymbol(e?.target?.value)}
              className="flex-1"
            />
            <Button
              variant="default"
              size="sm"
              onClick={addToWatchlist}
              disabled={!newSymbol}
            >
              <Icon name="Check" size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAddForm(false);
                setNewSymbol('');
              }}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}
      {/* Watchlist Items */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {watchlist?.map((item) => (
          <div
            key={item?.symbol}
            className={`bg-muted rounded-lg p-3 cursor-pointer transition-colors hover:bg-muted/80 ${
              selectedSymbol === item?.symbol ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => onSymbolSelect(item?.symbol)}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground text-sm">{item?.symbol}</span>
                {item?.alert?.enabled && (
                  <Icon 
                    name="Bell" 
                    size={12} 
                    className="text-warning" 
                  />
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e?.stopPropagation();
                    toggleAlert(item?.symbol);
                  }}
                  className="p-1"
                >
                  <Icon 
                    name={item?.alert?.enabled ? "BellRing" : "Bell"} 
                    size={12} 
                    className={item?.alert?.enabled ? "text-warning" : "text-muted-foreground"}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e?.stopPropagation();
                    removeFromWatchlist(item?.symbol);
                  }}
                  className="p-1 text-error hover:text-error"
                >
                  <Icon name="Trash2" size={12} />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">
                {formatPrice(item?.price)}
              </span>
              <span className={`text-sm font-medium ${
                item?.change >= 0 ? 'text-success' : 'text-error'
              }`}>
                {item?.change >= 0 ? '+' : ''}{item?.change?.toFixed(2)}%
              </span>
            </div>

            {item?.alert?.enabled && item?.alert?.price && (
              <div className="mt-2 pt-2 border-t border-border">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="AlertTriangle" size={10} />
                  <span>
                    Alert {item?.alert?.type} ${item?.alert?.price?.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {watchlist?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Star" size={48} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">No symbols in watchlist</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddForm(true)}
            className="mt-2"
          >
            Add your first symbol
          </Button>
        </div>
      )}
      {/* Quick Actions */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="RefreshCw" size={14} />
          Refresh
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Settings" size={14} />
          Alerts
        </Button>
      </div>
    </div>
  );
};

export default WatchlistWidget;